/**
 * Export + share core — the framework-free logic both export/share surfaces
 * delegate to: the viewer's `useExportShare` and the workbench's
 * `ExportShareMenu` primitive. One implementation of filename derivation,
 * share-doc assembly, and the clipboard-link flow (size tiers, error
 * shapes), so the two surfaces can't drift.
 */
import type { Viewport } from "@archlang/scene/board";
import type { Vfs } from "@archlang/viewer-core";
import type { Assets } from "./workspaceAssets.js";
import type { ExportFocalOptions } from "./board/exportPng.js";
import {
  vfsToEntries,
  assetsToShareEntries,
  encodeShareDoc,
  shareSizeTier,
  SHARE_PARAM,
  type ShareDoc,
  type ShareOutcome,
} from "./shareLink.js";

/** The slice of a board handle that captures need — both the viewer's
 *  `InfiniteBoardHandle` and the workbench's `ArchitectureViewHandle`
 *  satisfy it. */
export interface BoardCaptureHandle {
  getViewport(): Viewport | null;
  exportFocalAsPng(opts?: ExportFocalOptions): Promise<void>;
  exportFocalAsSvg(opts?: ExportFocalOptions): Promise<void>;
  copyFocalToClipboard(opts?: ExportFocalOptions): Promise<void>;
}

/**
 * Filesystem-safe basename from the current focal space (or the workspace
 * label as a fallback). Qualified names contain dots (Platform.AuthService);
 * replace them with `_` so the only `.` in the filename is before the
 * extension and the OS doesn't treat e.g. `.AuthService` as the extension.
 */
export function focalBasename(
  viewport: Viewport | null,
  qualifiedNameById: ReadonlyMap<string, string> | undefined,
  fallbackLabel: string,
): string {
  const lastId = viewport?.focalPath[viewport.focalPath.length - 1];
  const rawName = (lastId && qualifiedNameById?.get(lastId)) ?? fallbackLabel;
  const safe = rawName
    .replace(/\./g, "_")
    .replace(/[^a-z0-9_-]+/gi, "-")
    .replace(/^[-_]+|[-_]+$/g, "");
  return safe || "archlang";
}

export interface ShareDocInput {
  readonly vfs: Vfs;
  /** Binary assets (images) to embed in the link. Omit / empty for none. */
  readonly assets?: Assets;
  readonly label: string;
  /** Include focal/pan/zoom + selection ("share view"). Omit for workspace-only. */
  readonly view?: {
    readonly viewport: Viewport;
    readonly selectedModuleId: string | null;
    readonly selectedEdgeId: string | null;
    readonly selectedInterfaceKey: string | null;
  };
  readonly compare?: {
    readonly vfs: Vfs;
    readonly label: string;
    readonly swapped: boolean;
  } | null;
}

export function buildWorkspaceShareDoc(input: ShareDocInput): ShareDoc {
  const { vfs, assets, label, view, compare } = input;
  return {
    files: vfsToEntries(vfs),
    assets: assets && assets.size > 0 ? assetsToShareEntries(assets) : undefined,
    label,
    view: view
      ? {
          viewport: {
            focalPath: view.viewport.focalPath,
            center: view.viewport.center,
            zoom: view.viewport.zoom,
          },
          sel: {
            module: view.selectedModuleId ?? undefined,
            edge: view.selectedEdgeId ?? undefined,
            iface: view.selectedInterfaceKey ?? undefined,
          },
        }
      : undefined,
    compare: compare
      ? {
          files: vfsToEntries(compare.vfs),
          label: compare.label,
          swapped: compare.swapped,
        }
      : undefined,
  };
}

/** Encode `doc` as a `#s=` link and copy it; every failure mode collapses
 *  to an `{ ok: false }` outcome the menu can surface inline. `opts.path`
 *  overrides the link's pathname (scratch shares point at `/scratch`). */
export async function copyShareLinkToClipboard(
  doc: ShareDoc,
  opts: { readonly path?: string } = {},
): Promise<ShareOutcome> {
  if (typeof window === "undefined") {
    return { ok: false, message: "Sharing isn't available here." };
  }
  const encoded = encodeShareDoc(doc);
  const tier = shareSizeTier(encoded.length);
  if (tier === "huge") {
    return {
      ok: false,
      message:
        "This workspace is too large for a link. Download it as a file to share instead.",
    };
  }
  if (!navigator.clipboard?.writeText) {
    return { ok: false, message: "Clipboard access isn't available in this browser." };
  }
  const base = `${window.location.origin}${opts.path ?? window.location.pathname}`;
  try {
    await navigator.clipboard.writeText(`${base}#${SHARE_PARAM}=${encoded}`);
  } catch {
    return { ok: false, message: "Couldn't write to the clipboard." };
  }
  return {
    ok: true,
    message:
      tier === "long"
        ? "Link copied — it's long and may break in some chat apps."
        : "Link copied to clipboard.",
  };
}
